import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { MicIcon } from './Icons.js';
import { VoiceRecorder } from './VoiceRecorder.js';

/**
 * Answer the question in front of you out loud.
 *
 * The questions themselves are the format's own — written for the job, and the
 * same ones on screen and on the printed worksheet. Nothing here generates them:
 * a model inventing the questions would be a downgrade from a clinical form to
 * an improvisation, and this app's standing rule is that models do not author
 * the user's account of their own experience. What the model does here is
 * transcribe, and that is all it does.
 *
 * Collapsed to a single line until asked for. Typing is still the default path
 * and the recorder is a big button with a timer — offering it permanently would
 * make every step look like it wanted a performance.
 *
 * The transcript is APPENDED rather than substituted. Speaking is for adding the
 * bit you could not be bothered to type, often onto something already written,
 * and silently replacing a sentence someone typed with whatever a model heard is
 * the sort of thing you only forgive once.
 */
export function SpeakAnswer({
  onText,
  hasExistingText,
}: {
  onText: (text: string) => void;
  hasExistingText: boolean;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handle(blob: Blob): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const { text } = await api.transcribeOnce(blob);
      if (text.trim() === '') {
        setError("Nothing came back from that recording — it may have been too quiet.");
        return;
      }
      onText(text.trim());
      setOpen(false);
    } catch (e) {
      // Left open on failure, so the answer is not lost behind a collapsed
      // control the moment something goes wrong.
      setError(e instanceof ApiError ? e.message : 'Could not transcribe that.');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button className="btn btn-secondary btn-block" onClick={() => setOpen(true)}>
        <MicIcon />
        {hasExistingText ? 'Add to this out loud' : 'Answer out loud'}
      </button>
    );
  }

  return (
    <div className="card stack-sm stack">
      {error && <div className="banner banner-caution">{error}</div>}

      {busy ? (
        <div className="row" style={{ alignItems: 'center', gap: 'var(--space-3)' }}>
          <span className="spinner" />
          <span className="muted">Writing that down…</span>
        </div>
      ) : (
        <>
          <VoiceRecorder onComplete={(r) => void handle(r.blob)} />
          <button className="btn btn-ghost btn-block" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </>
      )}
    </div>
  );
}
